import { buildBaziReport } from '../bazi-engine/core/bazi_engine';
import { calculateBaziAsync as calculateBaziReportAsync } from '../bazi-engine/api/bazi_api';
import { THIEN_CAN_ORDER, DIA_CHI_ORDER, getCanChiYear } from '../bazi-engine/core/canchi_year';
import { getCanChiDay as getCanonicalCanChiDay } from '../bazi-engine/core/canchi_day';
import {
  LUC_HOP,
  TU_HAI,
  TU_PHA,
  TU_XUNG,
  checkLucHop,
  checkTuHai,
  checkTuPha,
  checkTuXung
} from '../bazi-engine/core/interactions';

export const CAN_NAMES = {
  0: 'Giáp', 1: 'Ất', 2: 'Bính', 3: 'Đinh', 4: 'Mậu',
  5: 'Kỷ', 6: 'Canh', 7: 'Tân', 8: 'Nhâm', 9: 'Quý'
};

export const CAN_NAMES_EN = {
  0: 'Jia', 1: 'Yi', 2: 'Bing', 3: 'Ding', 4: 'Wu',
  5: 'Ji', 6: 'Geng', 7: 'Xin', 8: 'Ren', 9: 'Gui'
};

export const CHI_NAMES = {
  0: 'Tý', 1: 'Sửu', 2: 'Dần', 3: 'Mão', 4: 'Thìn', 5: 'Tỵ',
  6: 'Ngọ', 7: 'Mùi', 8: 'Thân', 9: 'Dậu', 10: 'Tuất', 11: 'Hợi'
};

export const CHI_NAMES_EN = {
  0: 'Rat', 1: 'Ox', 2: 'Tiger', 3: 'Rabbit', 4: 'Dragon', 5: 'Snake',
  6: 'Horse', 7: 'Goat', 8: 'Monkey', 9: 'Rooster', 10: 'Dog', 11: 'Pig'
};

export const ELEMENT_MAP = {
  0: 'Mộc', 1: 'Mộc', 2: 'Hỏa', 3: 'Hỏa', 4: 'Thổ',
  5: 'Thổ', 6: 'Kim', 7: 'Kim', 8: 'Thủy', 9: 'Thủy'
};

const ELEMENT_FROM_ENGINE = {
  Moc: 'Mộc',
  Hoa: 'Hỏa',
  Tho: 'Thổ',
  Kim: 'Kim',
  Thuy: 'Thủy'
};

const normalizeEngineElement = (element) => (
  ELEMENT_FROM_ENGINE[element] || element
);

export const ELEMENT_COLOR = {
  'Mộc': '#22c55e',
  'Hỏa': '#ef4444',
  'Thổ': '#eab308',
  'Kim': '#f59e0b',
  'Thủy': '#3b82f6'
};

export const CHI_ELEMENT = {
  'Tý': 'Thủy',
  'Sửu': 'Thổ',
  'Dần': 'Mộc',
  'Mão': 'Mộc',
  'Thìn': 'Thổ',
  'Tỵ': 'Hỏa',
  'Ngọ': 'Hỏa',
  'Mùi': 'Thổ',
  'Thân': 'Kim',
  'Dậu': 'Kim',
  'Tuất': 'Thổ',
  'Hợi': 'Thủy'
};

const relationPairsToIndexes = (pairs) => (
  pairs.map(([a, b]) => [DIA_CHI_ORDER.indexOf(a), DIA_CHI_ORDER.indexOf(b)])
);

// Compatibility export for UI consumers. The canonical relationship data lives
// in bazi-engine/core/interactions; do not define a second relationship table here.
export const CHI_RELATIONS = {
  hop: relationPairsToIndexes(Object.entries(LUC_HOP)),
  xung: relationPairsToIndexes(TU_XUNG),
  hai: relationPairsToIndexes(TU_HAI),
  pha: relationPairsToIndexes(TU_PHA)
};

export const ELEMENT_CLASH = {
  'Mộc': 'Thổ',
  'Thổ': 'Thủy',
  'Thủy': 'Hỏa',
  'Hỏa': 'Kim',
  'Kim': 'Mộc'
};

const ELEMENT_GENERATES = {
  'Mộc': 'Hỏa',
  'Hỏa': 'Thổ',
  'Thổ': 'Kim',
  'Kim': 'Thủy',
  'Thủy': 'Mộc'
};

const getElementGenerator = (element) => (
  Object.entries(ELEMENT_GENERATES).find(([, target]) => target === element)?.[0] || element
);

export const getStemName = (canIdx, language = 'vi') => (
  (language === 'en' ? CAN_NAMES_EN[canIdx] : CAN_NAMES[canIdx]) || CAN_NAMES[canIdx] || ''
);

export const getBranchName = (chiIdx, language = 'vi') => (
  (language === 'en' ? CHI_NAMES_EN[chiIdx] : CHI_NAMES[chiIdx]) || CHI_NAMES[chiIdx] || ''
);

export const getCanChiLabel = (canIdx, chiIdx, language = 'vi') => {
  const canLabel = getStemName(canIdx, language);
  const chiLabel = getBranchName(chiIdx, language);
  return `${canLabel}-${chiLabel}`;
};

const RELATION_LABELS = {
  vi: {
    compatible: 'Hợp 👫',
    clash: 'Xung ⚡',
    harm: 'Hại 💔',
    break: 'Phá 💥',
    independent: 'Độc Lập'
  },
  en: {
    compatible: 'Compatible 👫',
    clash: 'Clash ⚡',
    harm: 'Harm 💔',
    break: 'Disruptive 💥',
    independent: 'Independent'
  }
};

const OUTLOOK_LABELS = {
  vi: {
    positive: 'Tích cực 🌟',
    caution: 'Cần cẩn thận ⚠️',
    neutral: 'Bình thường'
  },
  en: {
    positive: 'Positive 🌟',
    caution: 'Use caution ⚠️',
    neutral: 'Neutral'
  }
};

const CONSTITUTION_LABELS_VI = {
  strong: 'Thân Mạnh 💪',
  weak: 'Thân Yếu 😔',
  balanced: 'Bình Hòa ⚖️'
};

const mapEngineStrength = (engineCounts) => ({
  'Mộc': engineCounts?.Moc || 0,
  'Hỏa': engineCounts?.Hoa || 0,
  'Thổ': engineCounts?.Tho || 0,
  'Kim': engineCounts?.Kim || 0,
  'Thủy': engineCounts?.Thuy || 0
});

const findEngineBodyStrength = (engineReport) => {
  const reasoning = engineReport?.dung_than_reasoning;
  if (!Array.isArray(reasoning)) return null;

  const value = reasoning.find((item) => item?.factor === 'BodyStrength')?.value;
  if (!value || typeof value !== 'string') return null;

  const normalized = value.toLowerCase();
  if (normalized === 'strong' || normalized === 'weak') return normalized;
  if (normalized === 'neutral') return 'balanced';

  return null;
};

const buildPillarFromEngine = (pillar) => {
  const canIdx = THIEN_CAN_ORDER.indexOf(pillar.can);
  const chiIdx = DIA_CHI_ORDER.indexOf(pillar.chi);
  const element = normalizeEngineElement(pillar.can_element);
  const chiElement = normalizeEngineElement(pillar.chi_element);

  return {
    can: canIdx,
    chi: chiIdx,
    canName: CAN_NAMES[canIdx],
    chiName: CHI_NAMES[chiIdx],
    canNameLocalized: {
      vi: getStemName(canIdx, 'vi'),
      en: getStemName(canIdx, 'en')
    },
    chiNameLocalized: {
      vi: getBranchName(chiIdx, 'vi'),
      en: getBranchName(chiIdx, 'en')
    },
    canChiLabelLocalized: {
      vi: getCanChiLabel(canIdx, chiIdx, 'vi'),
      en: getCanChiLabel(canIdx, chiIdx, 'en')
    },
    element,
    chiElement
  };
};

export const getCanChiFromYear = (year) => {
  const canonical = getCanChiYear(year);
  return { can: canonical.canIndex, chi: canonical.chiIndex };
};

const getAdapterLocalDateParts = (date) => {
  if (date instanceof Date) {
    return {
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate()
    };
  }
  return date;
};

export const getCanChiDay = (date, options = {}) => {
  const canonical = getCanonicalCanChiDay(getAdapterLocalDateParts(date), options);
  const stem = CAN_NAMES[canonical.canIndex];
  const branch = CHI_NAMES[canonical.chiIndex];

  return {
    stem,
    branch,
    label: `${stem} ${branch}`
  };
};

export const getElement = (can) => ELEMENT_MAP[can];

export const getChiElement = (chiIdx) => CHI_ELEMENT[CHI_NAMES[chiIdx]];

const engineBranchFromIndex = (chiIndex) => (
  Number.isInteger(chiIndex) && chiIndex >= 0 && chiIndex < DIA_CHI_ORDER.length
    ? DIA_CHI_ORDER[chiIndex]
    : null
);

export const getChiRelation = (chi1, chi2) => {
  const branch1 = engineBranchFromIndex(chi1);
  const branch2 = engineBranchFromIndex(chi2);
  if (!branch1 || !branch2) return 'independent';
  if (checkLucHop(branch1, branch2)) return 'compatible';
  if (checkTuXung(branch1, branch2)) return 'clash';
  if (checkTuHai(branch1, branch2)) return 'harm';
  if (checkTuPha(branch1, branch2)) return 'break';
  return 'independent';
};

export const getChiRelationLabel = (relationKey, language = 'vi') => (
  RELATION_LABELS[language]?.[relationKey]
  || RELATION_LABELS.vi[relationKey]
  || RELATION_LABELS.vi.independent
);

export const getOutlookLabel = (outlookKey, language = 'vi') => (
  OUTLOOK_LABELS[language]?.[outlookKey]
  || OUTLOOK_LABELS.vi[outlookKey]
  || OUTLOOK_LABELS.vi.neutral
);

export const getTopElementsFromStrength = (strength = {}) => {
  const entries = Object.entries(strength || {});
  if (!entries.length) return [];
  const maxValue = Math.max(...entries.map(([, value]) => value));
  return entries
    .filter(([, value]) => value === maxValue)
    .map(([element]) => element);
};

const chiIndexFromName = (chiName) => {
  if (Number.isInteger(chiName) && chiName >= 0 && chiName < 12) return chiName;
  const entry = Object.entries(CHI_NAMES).find(([, name]) => name === chiName);
  return entry ? Number(entry[0]) : null;
};

const relationsBetweenChiNames = (chiA, chiB) => {
  const aIdx = chiIndexFromName(chiA);
  const bIdx = chiIndexFromName(chiB);
  if (aIdx == null || bIdx == null) return [];
  const branchA = engineBranchFromIndex(aIdx);
  const branchB = engineBranchFromIndex(bIdx);
  if (!branchA || !branchB) return [];

  return [
    checkTuXung(branchA, branchB) ? 'xung' : null,
    checkTuPha(branchA, branchB) ? 'pha' : null,
    checkTuHai(branchA, branchB) ? 'hai' : null,
    checkLucHop(branchA, branchB) ? 'hop' : null
  ].filter(Boolean);
};

const mergeYearRelations = (relations = []) => {
  const merged = {};
  relations.forEach((r) => {
    if (!r?.type || !r?.pair) return;
    const key = `${r.type}:${r.pair[0]}-${r.pair[1]}`;
    if (!merged[key]) merged[key] = { ...r, hits: [] };
    if (Array.isArray(r.hits)) merged[key].hits.push(...r.hits);
  });
  return Object.values(merged).map((r) => ({
    ...r,
    hits: Array.from(new Set(r.hits))
  }));
};

const buildBaziFromReport = (report) => {
  const pillars = {
    year: buildPillarFromEngine(report.four_pillars.year),
    month: buildPillarFromEngine(report.four_pillars.month),
    day: buildPillarFromEngine(report.four_pillars.day),
    hour: buildPillarFromEngine(report.four_pillars.hour)
  };

  const dayMasterIndex = THIEN_CAN_ORDER.indexOf(report.nhat_chu.can);
  const dayMasterElement = normalizeEngineElement(report.nhat_chu.ngu_hanh);

  return {
    pillars,
    dayMaster: dayMasterIndex,
    dayMasterElement,
    dayMasterLabelLocalized: {
      vi: getStemName(dayMasterIndex, 'vi'),
      en: getStemName(dayMasterIndex, 'en')
    },
    engineReport: report
  };
};

/**
 * Calculate Bazi chart synchronously from input.
 * @param {Date} birthDate - Valid Date object.
 * @param {number} birthHour - Hour in range [0, 24).
 * @param {{ timezoneOffsetMinutes?: number, gender?: string }} [options]
 * @returns {object|null}
 */
export const calculateBazi = (birthDate, birthHour, options = {}) => {
  try {
    if (!(birthDate instanceof Date) || Number.isNaN(birthDate.getTime())) {
      throw new Error('Invalid input: birthDate must be a valid Date');
    }
    if (!Number.isFinite(birthHour)) {
      throw new Error('Invalid input: birthHour must be a finite number');
    }

    const report = buildBaziReport({
      birthDate,
      birthHour,
      timezoneOffsetMinutes: options.timezoneOffsetMinutes,
      gender: options.gender
    });

    return buildBaziFromReport(report);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[calculateBazi] Failed to build report', error);
    return null;
  }
};

/**
 * Calculate Bazi chart asynchronously from input.
 * @param {Date} birthDate - Valid Date object.
 * @param {number} birthHour - Hour in range [0, 24).
 * @param {{ timezoneOffsetMinutes?: number, gender?: string }} [options]
 * @returns {Promise<object|null>}
 */
export const calculateBaziAsync = async (birthDate, birthHour, options = {}) => {
  try {
    if (!(birthDate instanceof Date) || Number.isNaN(birthDate.getTime())) {
      throw new Error('Invalid input: birthDate must be a valid Date');
    }
    if (!Number.isFinite(birthHour)) {
      throw new Error('Invalid input: birthHour must be a finite number');
    }

    const report = await calculateBaziReportAsync({
      birthDate,
      birthHour,
      timezoneOffsetMinutes: options.timezoneOffsetMinutes,
      gender: options.gender
    });

    return buildBaziFromReport(report);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[calculateBaziAsync] Failed to build report', error);
    return null;
  }
};

export const calculateElementStrength = (bazi) => {
  const strength = { 'Mộc': 0, 'Hỏa': 0, 'Thổ': 0, 'Kim': 0, 'Thủy': 0 };

  Object.values(bazi.pillars).forEach((pillar) => {
    strength[pillar.element] += 1;

    if (pillar.chiElement) {
      strength[pillar.chiElement] += 1;
    }
  });

  return strength;
};

/**
 * Analyze Bazi chart for strength, balance, and guidance.
 * @param {object} bazi
 * @returns {object}
 */
export const analyzeBazi = (bazi) => {
  const engineCounts = bazi.engineReport?.five_elements;
  const strength = engineCounts ? mapEngineStrength(engineCounts) : calculateElementStrength(bazi);
  const totalStrength = Object.values(strength).reduce((sum, value) => sum + value, 0);
  const dayMasterStrength = strength[bazi.dayMasterElement] || 0;
  const generatorElement = getElementGenerator(bazi.dayMasterElement);
  const supportStrength = dayMasterStrength + (strength[generatorElement] || 0);
  const relativeStrength = totalStrength > 0 ? dayMasterStrength / totalStrength : 0;
  const supportRatio = totalStrength > 0 ? supportStrength / totalStrength : 0;
  const monthElement = bazi.pillars?.month?.chiElement || bazi.pillars?.month?.element;
  const monthSupport = monthElement
    ? (monthElement === bazi.dayMasterElement || monthElement === generatorElement)
    : false;
  const values = Object.values(strength);
  const min = Math.min(...values);
  const max = Math.max(...values);

  const engineBodyStrength = findEngineBodyStrength(bazi.engineReport);

  let constitutionKey = engineBodyStrength || 'balanced';
  let recommendationKey = 'balance';

  if (engineBodyStrength === 'strong') {
    recommendationKey = 'reduceSelfElement';
  } else if (engineBodyStrength === 'weak') {
    recommendationKey = 'nourishSelfElement';
  } else {
    const isStrong = supportRatio > 0.6 && monthSupport && dayMasterStrength >= max - 0.5;
    const isWeak = supportRatio < 0.35 && !monthSupport && dayMasterStrength <= min + 0.5;

    if (isStrong) {
      constitutionKey = 'strong';
      recommendationKey = 'reduceSelfElement';
    } else if (isWeak) {
      constitutionKey = 'weak';
      recommendationKey = 'nourishSelfElement';
    }
  }

  const constitution = CONSTITUTION_LABELS_VI[constitutionKey] || CONSTITUTION_LABELS_VI.balanced;
  const recommendation = recommendationKey === 'reduceSelfElement'
    ? `Cần khắc chế, tránh bổ sung quá nhiều ${bazi.dayMasterElement}`
    : recommendationKey === 'nourishSelfElement'
      ? `Cần bổ sung ${bazi.dayMasterElement}`
      : 'Cân bằng phát triển';

  const balance = Object.entries(strength).reduce((acc, [element, value]) => {
    const percent = totalStrength > 0 ? (value / totalStrength) * 100 : 0;
    acc[element] = Math.round(percent * 10) / 10;
    return acc;
  }, {});

  const balanceText = Object.entries(balance).reduce((acc, [element, value]) => {
    acc[element] = Number.isFinite(value) ? value.toFixed(1) : '0.0';
    return acc;
  }, {});

  return {
    strength,
    balance,
    balanceText,
    constitution,
    recommendation,
    constitutionKey,
    recommendationKey,
    relativeStrength
  };
};

export const forecastYear = (bazi, year, language = 'vi') => {
  const yearCC = getCanChiFromYear(year);
  const yearElement = getElement(yearCC.can);
  const yearChiName = CHI_NAMES[yearCC.chi];

  const natalBranches = [
    { pillar: 'year', chi: bazi.pillars?.year?.chi },
    { pillar: 'month', chi: bazi.pillars?.month?.chi },
    { pillar: 'day', chi: bazi.pillars?.day?.chi },
    { pillar: 'hour', chi: bazi.pillars?.hour?.chi }
  ].filter((x) => x.chi !== null && x.chi !== undefined);

  const relationsRaw = natalBranches.flatMap((nb) => {
    const types = relationsBetweenChiNames(yearChiName, nb.chi);
    if (!types.length) return [];
    const natalChiName = Number.isInteger(nb.chi) ? CHI_NAMES[nb.chi] : nb.chi;
    return types.map((type) => ({
      type,
      pair: [yearChiName, natalChiName],
      hits: [nb.pillar]
    }));
  });

  const relationsWithNatal = mergeYearRelations(relationsRaw);

  const relationPriority = ['xung', 'hai', 'hop', 'pha'];
  const hitCounts = relationsWithNatal.reduce((acc, relation) => {
    const hits = relation?.hits?.length || 1;
    acc[relation.type] = Math.max(acc[relation.type] || 0, hits);
    return acc;
  }, {});
  const maxHits = Object.values(hitCounts).length
    ? Math.max(...Object.values(hitCounts))
    : 0;

  const primaryRelation = [...relationsWithNatal]
    .filter((relation) => (relation?.hits?.length || 1) === maxHits)
    .sort((a, b) => (relationPriority.indexOf(a.type) + 1 || 99)
      - (relationPriority.indexOf(b.type) + 1 || 99))
    .find((relation) => relationPriority.includes(relation.type));
  const relationKey = primaryRelation ? {
    xung: 'clash',
    pha: 'break',
    hai: 'harm',
    hop: 'compatible'
  }[primaryRelation.type] : getChiRelation(bazi.pillars.day.chi, yearCC.chi);

  let outlookKey = 'neutral';
  if (yearElement === bazi.dayMasterElement) {
    outlookKey = 'positive';
  } else if (yearElement === ELEMENT_CLASH[bazi.dayMasterElement]) {
    outlookKey = 'caution';
  }

  const yearPillarLocalized = {
    vi: getCanChiLabel(yearCC.can, yearCC.chi, 'vi'),
    en: getCanChiLabel(yearCC.can, yearCC.chi, 'en')
  };

  return {
    year,
    yearPillar: yearPillarLocalized[language] || yearPillarLocalized.vi,
    yearPillarLocalized,
    yearElement,
    elementBasis: 'heavenly_stem',
    relationKey,
    relation: getChiRelationLabel(relationKey, language),
    relationsWithNatal,
    outlookKey,
    outlook: getOutlookLabel(outlookKey, language)
  };
};
